import React from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import './LegalPage.css';

const PrivacyPage: React.FC = () => {
    const { t } = useTranslation();

    return (
        <div className="legal-page">
            <div className="legal-container">
                <header className="legal-header">
                    <h1>{t('privacy.title')}</h1>
                    <p className="legal-subtitle">{t('privacy.subtitle')}</p>
                    <p className="legal-date">{t('privacy.lastUpdated')}: 10.01.2025</p>
                </header>

                <section className="legal-section">
                    <h2>1. {t('privacy.sections.controller.title')}</h2>
                    <address className="legal-address">
                        Hofsync GbR i. Gr.<br />
                        Altenberger Gasse 9<br />
                        53639 Königswinter<br />
                        Deutschland
                    </address>
                    <p>E-Mail: <a href="mailto:info@hofsync.com">info@hofsync.com</a></p>
                </section>

                <section className="legal-section">
                    <h2>2. {t('privacy.sections.processing.title')}</h2>
                    <p>{t('privacy.sections.processing.onBehalf')}</p>
                    <p>{t('privacy.sections.processing.ownPurposes')}</p>
                </section>

                <section className="legal-section">
                    <h2>3. {t('privacy.sections.purposes.title')}</h2>
                    <p>{t('privacy.sections.purposes.intro')}</p>
                    <ul className="legal-list">
                        <li>{t('privacy.sections.purposes.platform')}</li>
                        <li>{t('privacy.sections.purposes.documents')}</li>
                        <li>{t('privacy.sections.purposes.userManagement')}</li>
                        <li>{t('privacy.sections.purposes.security')}</li>
                    </ul>
                </section>

                <section className="legal-section">
                    <h2>4. {t('privacy.sections.dataCategories.title')}</h2>
                    <p>{t('privacy.sections.dataCategories.intro')}</p>
                    <ul className="legal-list">
                        <li>{t('privacy.sections.dataCategories.customer')}</li>
                        <li>{t('privacy.sections.dataCategories.operational')}</li>
                        <li>{t('privacy.sections.dataCategories.documents')}</li>
                        <li>{t('privacy.sections.dataCategories.employee')}</li>
                        <li>{t('privacy.sections.dataCategories.auth')}</li>
                        <li>{t('privacy.sections.dataCategories.logs')}</li>
                    </ul>
                </section>

                <section className="legal-section">
                    <h2>5. {t('privacy.sections.legalBasis.title')}</h2>
                    <p>{t('privacy.sections.legalBasis.intro')}</p>
                    <ul className="legal-list">
                        <li>{t('privacy.sections.legalBasis.contract')}</li>
                        <li>{t('privacy.sections.legalBasis.legal')}</li>
                        <li>{t('privacy.sections.legalBasis.legitimate')}</li>
                    </ul>
                </section>

                <section className="legal-section">
                    <h2>6. {t('privacy.sections.processors.title')}</h2>
                    <p>{t('privacy.sections.processors.intro')}</p>
                    <ul className="legal-list">
                        <li><strong>Amazon Web Services EMEA SARL</strong> – {t('privacy.sections.processors.aws')}</li>
                        <li><strong>Microsoft Ireland Operations Limited</strong> – {t('privacy.sections.processors.microsoft')}</li>
                        <li><strong>Google Cloud EMEA / Google Ireland Limited</strong> – {t('privacy.sections.processors.google')}</li>
                        <li><strong>Twilio Ireland Limited</strong> – {t('privacy.sections.processors.twilio')}</li>
                        <li><strong>Elastic N.V.</strong> – {t('privacy.sections.processors.elastic')}</li>
                    </ul>
                    <p className="legal-highlight">{t('privacy.sections.processors.euOnly')}</p>
                    <p className="legal-highlight">{t('privacy.sections.processors.noTraining')}</p>
                </section>

                <section className="legal-section">
                    <h2>7. {t('privacy.sections.retention.title')}</h2>
                    <p>{t('privacy.sections.retention.duration')}</p>
                </section>

                <section className="legal-section">
                    <h2>8. {t('privacy.sections.rights.title')}</h2>
                    <p>{t('privacy.sections.rights.intro')}</p>
                    <ul className="legal-list">
                        <li>{t('privacy.sections.rights.access')}</li>
                        <li>{t('privacy.sections.rights.rectification')}</li>
                        <li>{t('privacy.sections.rights.erasure')}</li>
                        <li>{t('privacy.sections.rights.restriction')}</li>
                        <li>{t('privacy.sections.rights.portability')}</li>
                        <li>{t('privacy.sections.rights.objection')}</li>
                    </ul>
                    <p>{t('privacy.sections.rights.contact')}: <a href="mailto:datenschutz@hofsync.de">datenschutz@hofsync.de</a></p>
                </section>

                <section className="legal-section">
                    <h2>9. {t('privacy.sections.security.title')}</h2>
                    <p>{t('privacy.sections.security.measures')}</p>
                </section>

                {/* #3131 — before this section the policy named no cookies at
                    all, so the new notice would have pointed at a document
                    silent on the subject. That gap, not the banner, was the
                    actual compliance problem. Both cookies the SPA writes are
                    named here explicitly, with their lifetimes. */}
                <section className="legal-section" data-testid="privacy-cookies-section">
                    <h2>10. {t('privacy.sections.cookies.title')}</h2>
                    <p>{t('privacy.sections.cookies.intro')}</p>
                    <ul className="legal-list">
                        <li>{t('privacy.sections.cookies.session')}</li>
                        <li>{t('privacy.sections.cookies.csrf')}</li>
                        <li>{t('privacy.sections.cookies.consent')}</li>
                        <li>{t('privacy.sections.cookies.language')}</li>
                    </ul>
                    <p>{t('privacy.sections.cookies.localStorage')}</p>
                    <p className="legal-highlight">{t('privacy.sections.cookies.noTracking')}</p>
                    <p>{t('privacy.sections.cookies.control')}</p>
                </section>

                <section className="legal-section">
                    <h2>11. {t('privacy.sections.ipAddresses.title')}</h2>
                    <p>{t('privacy.sections.ipAddresses.processing')}</p>
                </section>

                <section className="legal-section">
                    <h2>12. {t('privacy.sections.changes.title')}</h2>
                    <p>{t('privacy.sections.changes.updates')}</p>
                </section>

                <div className="legal-back">
                    <Link to="/" className="legal-back-link">
                        {t('privacy.backToHome')}
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPage;
